import { observer } from "@legendapp/state/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useChapterInfo } from "@/hooks/useChapterInfo";
import { lesson$ } from "../../state/videoState";
import {
  feedCurrentIndex$,
  chapters$,
  requestStepForward,
  requestStepBackward,
  canStepForward,
  canStepBackward,
} from "../../state/feedState";
import { useIsMobile } from "../../hooks/useIsMobile";

const LessonNavigation = observer(function LessonNavigation() {
  const isMobile = useIsMobile();
  const lessonIndex = lesson$.currentLessonIndex.get();
  const chapter = chapters$.get()[feedCurrentIndex$.get()];
  // iOS hides the trailing "Full Video" lesson from the dot count when the
  // chapter has isFullVidHidden set (FeedNew.tsx:1018,
  // `visibleCount = isFullVidHidden ? totalCount - 1 : totalCount`). The feed
  // payload (ChapterFeedV2) doesn't carry the flag, so read it off the cached
  // chapter info (same hook VideoLayout already uses).
  const { data: chapterInfo } = useChapterInfo(chapter?.chapterID ?? null);

  if (!chapter) return null;

  const totalDots = chapterInfo?.isFullVidHidden
    ? Math.max(0, chapter.lessonIDs.length - 1)
    : chapter.lessonIDs.length;
  // Clamp the highlighted dot so it can never point past the reduced count
  // (e.g. if the user is on the hidden full-video lesson itself).
  const activeDot = Math.min(lessonIndex, totalDots - 1);

  return (
    <div className="flex items-center h-12 pb-4">
      <div className="w-12 shrink-0" />

      <div className="flex-1 flex items-center justify-center gap-3">
        {!isMobile && (
          <button
            type="button"
            onClick={() => requestStepBackward()}
            disabled={!canStepBackward()}
            className="flex w-8 h-8 items-center justify-center rounded-full bg-white/10 text-white transition-all duration-150 active:scale-90 disabled:opacity-30 disabled:pointer-events-none"
            aria-label="Previous"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}

        <div className="flex items-center gap-1.5">
          {Array.from({ length: totalDots }).map((_, i) => (
            <div
              key={i}
              className={`w-1.5 h-1.5 rounded-full transition-opacity duration-200 ${
                i === activeDot ? "bg-white opacity-100" : "bg-white opacity-30"
              }`}
            />
          ))}
        </div>

        {!isMobile && (
          <button
            type="button"
            onClick={() => requestStepForward()}
            disabled={!canStepForward()}
            className="flex w-8 h-8 items-center justify-center rounded-full bg-white/10 text-white transition-all duration-150 active:scale-90 disabled:opacity-30 disabled:pointer-events-none"
            aria-label="Next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="w-12 shrink-0" />
    </div>
  );
});

export { LessonNavigation };
